import { Component, ChangeDetectionStrategy, OnInit, Input, inject, ViewChild, ElementRef, AfterViewInit, OnDestroy, NgZone } from '@angular/core';
import { delay, filter, map, Subscription, take, tap } from 'rxjs';
import { CategoriesService } from './services/categories.service';
import { Navigation } from 'swiper/modules';
import Swiper from 'swiper';
import { WindowService } from '@shared/window';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { LocalizePipe } from '../../../i18n/pipes/lozalize.pipe';

@Component({
    selector: 'app-products-categories',
    templateUrl: './products-categories.component.html',
    styleUrls: ['./products-categories.component.scss'],
    providers: [CategoriesService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, AsyncPipe, TranslatePipe, LocalizePipe],
})
export class ProductsCategoriesComponent implements OnInit, AfterViewInit, OnDestroy {
    private zone = inject(NgZone);
    private activatedRoute = inject(ActivatedRoute);
    private categoriesService = inject(CategoriesService);
    private windowService = inject(WindowService);

    @Input() currentPage?: string;
    @ViewChild('swiper') swiperRef!: ElementRef<HTMLDivElement>;
    @ViewChild('swiperNavigationNext') swiperNavigationNextRef!: ElementRef<HTMLDivElement>;
    @ViewChild('swiperNavigationPrev') swiperNavigationPrevRef!: ElementRef<HTMLDivElement>;

    readonly isClient = this.windowService.isClient;

    private swiper?: Swiper;

    categories$ = this.categoriesService.categories$;

    private subscriptions$ = new Subscription();

    ngOnInit(): void {
        this.categoriesService.dispatch();
    }

    link(slug: string): string[] {
        return ['/products', 'category', slug, 'page', '1'];
    }

    get isPruductsPage(): boolean {
        return this.currentPage === 'products';
    }

    get isCategoryPage(): boolean {
        return this.currentPage === 'category';
    }

    ngAfterViewInit() {
        if (this.windowService.isBrowser) {
            this.subscriptions$.add(
                this.categories$
                    .pipe(filter((value) => value !== false))
                    .pipe(
                        take(1),
                        delay(0),
                        tap(() => this.initSwiper()),
                    )
                    .subscribe(),
            );
        }
    }

    ngOnDestroy(): void {
        this.swiper?.destroy();
        this.subscriptions$.unsubscribe();
    }

    private initSwiper() {
        this.zone.runOutsideAngular(() => {
            this.swiper = new Swiper(this.swiperRef.nativeElement, {
                modules: [Navigation],
                slidesPerView: 7,
                spaceBetween: 26,
                navigation: {
                    prevEl: this.swiperNavigationPrevRef.nativeElement,
                    nextEl: this.swiperNavigationNextRef.nativeElement,
                },
                centeredSlides: true,
                centeredSlidesBounds: true,
                slideToClickedSlide: true,
                watchOverflow: true,
                observer: true,
                observeParents: true,
                observeSlideChildren: true,
                breakpoints: {
                    0: {
                        slidesPerView: 2,
                    },
                    768: {
                        slidesPerView: 4,
                    },
                    1200: {
                        slidesPerView: 7,
                    },
                },
                threshold: 5,
                on: {
                    init: () => {
                        this.zone.run(() => {
                            this.swiperInitEvent();
                        });
                    },
                },
            });
        });
    }

    private swiperInitEvent() {
        const slug = this.activatedRoute.snapshot.params['slug'] ?? null;
        if (slug) {
            this.subscriptions$.add(
                this.categories$
                    .pipe(
                        filter((categories) => categories !== false),
                        take(1),
                        map((categories) => categories.findIndex((category) => category.slug === slug)),
                        delay(200),
                        tap((index) => this.swiper?.slideTo(index)),
                    )
                    .subscribe(),
            );
        }
    }
}
